import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  ArrowForwardRounded,
  KeyboardArrowLeftRounded,
  LockOutlined,
} from "@mui/icons-material";
import WorksuiteLoginBrandLogo from "../branding/WorksuiteLoginBrandLogo";
import {
  getArrivalExperience,
  readArrivalStyleFromLocation,
} from "./arrivalExperienceRegistry";

const fallbackSurface =
  "radial-gradient(circle at 18% 18%, rgba(72, 126, 255, 0.20), transparent 34%), radial-gradient(circle at 82% 70%, rgba(100, 71, 255, 0.16), transparent 38%), linear-gradient(145deg, #07090f 0%, #0b0f19 48%, #05070c 100%)";

const getCandidateAt = (candidates, index) =>
  candidates[Math.min(index, candidates.length - 1)] || "";

export default function WorksuiteArrivalExperienceShell({
  scene = "login",
  children,
  panelMaxWidth = 520,
  defaultExpanded = false,
  arrivalBrandHref = "https://worksuite.clikkle.com",
}) {
  const [styleId, setStyleId] = useState(readArrivalStyleFromLocation);
  const experience = useMemo(() => getArrivalExperience(scene, styleId), [scene, styleId]);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [assetIndex, setAssetIndex] = useState(0);
  const [assetFailed, setAssetFailed] = useState(false);

  const isImmersive = experience.style.presentation === "immersive-center";
  const desktopSrc = getCandidateAt(experience.desktopCandidates, assetIndex);
  const mobileSrc = getCandidateAt(experience.mobileCandidates, assetIndex);

  useEffect(() => {
    const nextStyle = readArrivalStyleFromLocation();
    setStyleId(nextStyle);
  }, [scene]);

  useEffect(() => {
    setAssetIndex(0);
    setAssetFailed(false);
    setExpanded(defaultExpanded);
  }, [scene, styleId, defaultExpanded]);

  useEffect(() => {
    if (assetFailed || typeof window === "undefined") return undefined;

    const preload = new Image();
    preload.decoding = "async";
    preload.src = window.matchMedia("(max-width: 720px)").matches
      ? mobileSrc
      : desktopSrc;

    return () => {
      preload.onload = null;
      preload.onerror = null;
    };
  }, [assetFailed, desktopSrc, mobileSrc]);

  const handleAssetError = () => {
    const nextIndex = assetIndex + 1;
    if (nextIndex < experience.desktopCandidates.length) {
      setAssetIndex(nextIndex);
      return;
    }
    setAssetFailed(true);
  };

  const collapsedJustify = isImmersive ? "center" : "end";

  return (
    <Box
      component="main"
      aria-label={experience.ariaLabel}
      data-arrival-style={styleId}
      sx={{
        minHeight: "100dvh",
        position: "relative",
        overflow: "hidden",
        display: "grid",
        alignItems: "center",
        justifyItems: {
          xs: "center",
          lg: expanded ? "end" : collapsedJustify,
        },
        px: { xs: 2, sm: 3, lg: 6 },
        py: { xs: 2, sm: 3 },
        isolation: "isolate",
        bgcolor: "#07090f",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{ position: "absolute", inset: 0, zIndex: -4, background: fallbackSurface }}
      />

      {!assetFailed && (
        <Box
          component="picture"
          aria-hidden="true"
          data-arrival-motion={expanded ? "authenticate" : "arrival"}
          sx={{
            position: "absolute",
            inset: "-4%",
            zIndex: -3,
            overflow: "hidden",
            pointerEvents: "none",
            transformOrigin: "center center",
            animation: expanded
              ? "worksuiteArrivalAuthenticateDrift 18s ease-in-out infinite alternate"
              : "worksuiteArrivalCinematicDrift 28s ease-in-out infinite alternate",
            willChange: "transform",
            "@keyframes worksuiteArrivalCinematicDrift": {
              from: { transform: "scale(1.045) translate3d(-0.45%, -0.3%, 0)" },
              to: { transform: "scale(1.085) translate3d(0.55%, 0.35%, 0)" },
            },
            "@keyframes worksuiteArrivalAuthenticateDrift": {
              from: { transform: "scale(1.07) translate3d(-0.2%, -0.15%, 0)" },
              to: { transform: "scale(1.105) translate3d(0.35%, 0.25%, 0)" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              inset: 0,
              animation: "none",
              transform: "none",
              willChange: "auto",
            },
          }}
        >
          <source media="(max-width: 720px)" srcSet={mobileSrc} />
          <Box
            component="img"
            src={desktopSrc}
            alt=""
            onError={handleAssetError}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: experience.position,
              userSelect: "none",
              pointerEvents: "none",
              transform: expanded ? "scale(1.018)" : "scale(1)",
              transition: "transform 900ms cubic-bezier(.2,.8,.2,1)",
              willChange: "transform",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                transform: "none",
                willChange: "auto",
              },
            }}
          />
        </Box>
      )}

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: -2,
          background: expanded
            ? {
                xs: "linear-gradient(180deg, rgba(4,7,12,.20), rgba(4,7,12,.72))",
                lg: "linear-gradient(90deg, rgba(4,7,12,.05) 0%, rgba(4,7,12,.14) 57%, rgba(4,7,12,.64) 100%)",
              }
            : isImmersive
              ? "linear-gradient(180deg, rgba(4,7,12,.04) 0%, rgba(4,7,12,.02) 55%, rgba(4,7,12,.20) 100%)"
              : {
                  xs: "linear-gradient(180deg, rgba(4,7,12,.14), rgba(4,7,12,.62))",
                  lg: "linear-gradient(90deg, rgba(4,7,12,.03) 0%, rgba(4,7,12,.08) 67%, rgba(4,7,12,.42) 100%)",
                },
          transition: "background 500ms ease",
        }}
      />

      {!expanded && isImmersive ? (
        <>
          <Box
            aria-label="Worksuite arrival"
            sx={{
              position: "absolute",
              inset: 0,
              color: "#11151d",
              textShadow: "0 1px 18px rgba(255,255,255,.32)",
              animation: "worksuiteArrivalReveal 760ms cubic-bezier(.2,.8,.2,1) both",
              "@keyframes worksuiteArrivalReveal": {
                from: { opacity: 0, transform: "translateY(12px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Box
              component="a"
              href={arrivalBrandHref}
              aria-label="Open product home"
              sx={{
                position: "absolute",
                left: "50%",
                top: { xs: "16%", sm: "18.5%", lg: "19.5%" },
                transform: "translateX(-50%)",
                width: { xs: 176, sm: 220, lg: 244 },
                display: "flex",
                justifyContent: "center",
                textDecoration: "none",
                borderRadius: 3,
                outlineOffset: 8,
                cursor: "pointer",
                "&:focus-visible": { outline: "2px solid currentColor" },
              }}
            >
              <WorksuiteLoginBrandLogo
                alt="Worksuite"
                sx={{
                  width: "100%",
                  maxHeight: { xs: 190, sm: 230, lg: 252 },
                  objectFit: "contain",
                  filter: "drop-shadow(0 12px 30px rgba(0,0,0,.13))",
                }}
              />
            </Box>

            <Typography
              sx={{
                position: "absolute",
                left: "50%",
                top: { xs: "49%", sm: "50.5%", lg: "51%" },
                transform: "translateX(-50%)",
                width: "min(88vw, 560px)",
                textAlign: "center",
                fontSize: { xs: 13, sm: 14, lg: 15 },
                fontWeight: 500,
                letterSpacing: ".01em",
                color: "inherit",
              }}
            >
              {experience.entranceSubtitle}
            </Typography>

            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: { xs: "56%", sm: "57%", lg: "57.5%" },
                transform: "translateX(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <IconButton
                aria-label={experience.entranceActionLabel}
                onClick={() => setExpanded(true)}
                sx={{
                  width: { xs: 56, sm: 62, lg: 66 },
                  height: { xs: 56, sm: 62, lg: 66 },
                  border: "1.5px solid currentColor",
                  color: "inherit",
                  bgcolor: "rgba(255,255,255,.055)",
                  backdropFilter: "blur(4px)",
                  WebkitBackdropFilter: "blur(4px)",
                  boxShadow: "0 8px 26px rgba(0,0,0,.08)",
                  transition: "transform 260ms cubic-bezier(.2,.8,.2,1), background-color 260ms ease, box-shadow 260ms ease",
                  "& .MuiSvgIcon-root": { transition: "transform 260ms ease" },
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.18)",
                    transform: "scale(1.055)",
                    boxShadow: "0 12px 34px rgba(0,0,0,.14)",
                    "& .MuiSvgIcon-root": { transform: "translateX(3px)" },
                  },
                  "&:active": { transform: "scale(.97)" },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                <ArrowForwardRounded fontSize="medium" />
              </IconButton>

              <Typography
                sx={{
                  mt: { xs: 1.4, sm: 1.65 },
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 500,
                  color: "inherit",
                }}
              >
                {experience.entranceActionLabel}
              </Typography>
            </Box>
          </Box>

          <Box
            aria-label="Worksuite trust statement"
            sx={{
              position: "absolute",
              left: "50%",
              bottom: { xs: 16, sm: 22, lg: 28 },
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: { xs: .8, sm: 1.2 },
              color: "rgba(255,255,255,.78)",
              fontSize: { xs: 10, sm: 12 },
              letterSpacing: ".02em",
              textShadow: "0 2px 12px rgba(0,0,0,.46)",
              whiteSpace: "nowrap",
              animation: "worksuiteTrustReveal 900ms 180ms ease both",
              "@keyframes worksuiteTrustReveal": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <LockOutlined sx={{ fontSize: 14 }} />
            <span>Secure</span><span>•</span><span>Private</span><span>•</span><span>Always Yours</span>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: expanded ? panelMaxWidth : 360,
            minHeight: expanded ? 410 : "auto",
            maxHeight: { xs: "calc(100dvh - 32px)", sm: "calc(100dvh - 48px)" },
            borderRadius: { xs: 4, sm: 5 },
            border: "1px solid rgba(255,255,255,.16)",
            background: "rgba(12,14,19,.18)",
            boxShadow: expanded
              ? "0 30px 90px rgba(0,0,0,.52), inset 0 1px 0 rgba(255,255,255,.08)"
              : "0 24px 70px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.10)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            overflow: "hidden",
            animation: expanded ? "worksuiteAuthSurfaceIn 560ms cubic-bezier(.2,.8,.2,1) both" : "none",
            "@keyframes worksuiteAuthSurfaceIn": {
              from: { opacity: 0, transform: "translateX(26px) scale(.985)" },
              to: { opacity: 1, transform: "translateX(0) scale(1)" },
            },
            transition: "max-width 520ms cubic-bezier(.2,.8,.2,1), background 320ms ease, box-shadow 320ms ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          {!expanded ? (
            <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 3.5, sm: 4.5 }, color: "#f7f9fc" }}>
              <Typography variant="h4" fontWeight={750} sx={{ letterSpacing: "-.035em", mb: 1 }}>
                {experience.entranceTitle}
              </Typography>
              <Typography sx={{ color: "rgba(244,247,255,.72)", lineHeight: 1.55, mb: 3 }}>
                {experience.entranceSubtitle}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardRounded />}
                onClick={() => setExpanded(true)}
                sx={{
                  py: 1.25,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 750,
                  bgcolor: "rgba(47,125,255,.92)",
                  boxShadow: "0 14px 34px rgba(47,125,255,.25)",
                }}
              >
                {experience.entranceActionLabel}
              </Button>
            </Box>
          ) : (
            <Box sx={{ position: "relative", maxHeight: "inherit", overflowY: "auto", overscrollBehavior: "contain" }}>
              <IconButton
                aria-label="Return to arrival view"
                onClick={() => setExpanded(false)}
                sx={{
                  position: "sticky",
                  top: 12,
                  left: 12,
                  zIndex: 3,
                  float: "left",
                  ml: 1.5,
                  mt: 1.5,
                  color: "rgba(255,255,255,.78)",
                  bgcolor: "rgba(5,8,14,.42)",
                  border: "1px solid rgba(255,255,255,.12)",
                  backdropFilter: "blur(14px)",
                  "&:hover": { bgcolor: "rgba(255,255,255,.10)" },
                }}
              >
                <KeyboardArrowLeftRounded />
              </IconButton>
              {children}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
